import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { Chapter, Prose, TheoryBox, MTQuote, Glossary, Term } from "@/components/mt/MTBox";
import { NoteImage } from "@/components/mt/NoteImage";
import { ChordBuilder } from "@/components/mt/ChordBuilder";
import { ChordTree } from "@/components/mt/ChordTree";
import { NextUp } from "@/components/Callouts";

export default function MusicTheory20() {
  const { lesson, course, accent } = findLesson("music-theory", "20");
  return (
    <LessonShell
      course={course}
      courseSlug="music-theory"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/music-theory/MT20-Slash-Add-Chords-Animated.mp4"
        caption="Slash chords (X/Y, bass change) & Add chords (add9, 6, add11)"
        hint="≈ 28 sec · watch first"
      />
      <Chapter number="20" eyebrow="Music Theory · Chapter Twenty" title="Two small modifications, two huge sounds">
        <Prose>
          <p>
            With triads built, almost every chord chart you'll ever read is within reach.
            But you'll quickly run into two pieces of notation that aren't pure triads: the{" "}
            <strong>slash chord</strong> (something like <span className="font-mono">C/E</span>{" "}
            or <span className="font-mono">G/B</span>) and the <strong>add chord</strong>{" "}
            (something like <span className="font-mono">Cadd9</span> or{" "}
            <span className="font-mono">Fadd11</span>).
          </p>
          <p>
            Both are tiny modifications to a triad. Both completely change how the chord
            sits in a progression. And neither involves stacking another third on top — that
            comes in the next chapter, with seventh chords. For now, our triad stays a
            triad. We're just going to change its <em>bass</em> or add a single{" "}
            <em>colour tone</em> to it.
          </p>
          <MTQuote>
            Add is generally used when you don't have a seventh, you won't have a seventh
            degree in the chord, and you're trying to add to the triad. So you're not going
            to remove the third, while a suspension or a suspended chord is where there is
            no third.
          </MTQuote>
        </Prose>
        <NoteImage
          src="/notes/music-theory/finals/20a-slash-chords-bass-note.png"
          caption="Slash chords explained: the part before the slash is the triad symbol (C major); the part after is the bass note (so C/E means C major with E in the bass — the 1st inversion). Slash chords let you build smooth bass lines independent of the harmony above."
        />
      </Chapter>

      <Chapter eyebrow="Slash chords" title="Reading the slash">
        <Prose>
          <p>
            A slash chord is written <strong>chord / bass</strong>. The letter on the left is
            the chord name. The letter on the right is the note that goes in the bass —
            usually a different note from the root. So{" "}
            <span className="font-mono text-amber-300">C/E</span> means: play a C major
            triad in the right hand, but put an <strong>E</strong> in the bass. And{" "}
            <span className="font-mono text-amber-300">G/B</span> means: play a G major
            triad, but put a <strong>B</strong> in the bass.
          </p>
          <p>
            Why do this? Because the bass note tells the listener where they are in the
            song, and changing it without changing the chord above is the simplest way to
            create a smooth, walking bass line. You keep the harmony static; you slide the
            bass underneath it.
          </p>
        </Prose>

        <Glossary>
          <Term name="Slash chord" def="A chord whose bass note is something other than its root. Written 'chord/bass' — e.g. C/E means a C triad with E in the bass." />
          <Term name="Inversion" def="A specific kind of slash chord — when the bass note is one of the chord's own three notes (the 3rd or the 5th), it's just an inversion." />
          <Term name="Pedal point" def="A bass note that holds steady while the chords change above it. Often written as a series of slash chords sharing the same bass." />
        </Glossary>
      </Chapter>

      <Chapter eyebrow="Two flavours of slash" title="Inversion vs. true slash">
        <Prose>
          <p>
            Slash chords come in two flavours, and it's worth knowing the difference.
          </p>
          <p>
            <strong>Flavour 1 — inversion.</strong> If the bass note already lives inside
            the triad — its third or its fifth — then the slash is just an inversion in
            disguise. <span className="font-mono">C/E</span> is C major's first inversion
            (E G C from the bottom up). <span className="font-mono">C/G</span> is C major's
            second inversion (G C E). All you've done is rearrange the three notes already
            in the chord.
          </p>
          <p>
            <strong>Flavour 2 — true slash.</strong> If the bass note is <em>not</em> in the
            triad, you're adding a fourth note from outside the chord. That's a more
            unusual sound — a triad floating over a foreign root. Famous examples include
            Am/F (an A-minor triad over an F bass — instantly cinematic, and really an
            Fmaj7), C/B♭ (turning a humble C major into a dominant-7th flavour), or C/D
            (a C triad over a D bass — the classic suspended pad sound).
          </p>
        </Prose>

        <TheoryBox label="The slash-chord rule">
          Read the right side of the slash <em>first</em> — that's your bass. Play the
          chord on the left in the right hand, on top. The whole point of the notation is
          to specify both halves independently.
        </TheoryBox>
      </Chapter>

      <Chapter eyebrow="The same triad, different basses" title="Try it with the chord tree">
        <Prose>
          <p>
            Below is the diatonic chord tree of C major. Every triad here can be slashed
            against any other note. Start with I (C major) and walk it through{" "}
            <span className="font-mono">C → C/E → C/G → C/B → Am</span>. The triad on top
            barely moves while the bass climbs C–E–G–B and then steps down into A. That's a
            slash-chord progression in its purest form. Reverse it —
            <span className="font-mono"> C → C/B → Am → Am/G → F</span> — and you have the
            descending line that carries half the ballads ever written.
          </p>
        </Prose>

        <ChordTree tonic="C" mode="major" />

        <Prose>
          <p>
            This trick — moving the bass under a static or slow-moving triad — is the
            engine behind a thousand pop songs. Beatles, Coldplay, Adele, Ludovico Einaudi.
            Once you hear it, you can't un-hear it.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="Add chords" title="Bolt one extra note onto the triad">
        <Prose>
          <p>
            An <strong>add chord</strong> is the second small modification. Take a normal
            major or minor triad and bolt <em>one</em> extra colour tone onto it — without
            adding a 7th. The result is "fuller than a triad, simpler than a 9th chord".
          </p>
          <p>
            The two add chords you'll meet most often are:
          </p>
          <ul className="space-y-1 text-[14px]">
            <li><strong>add9</strong> — a triad plus the 9th (the same note as the 2nd, but an octave up). C add9 = C E G D′. Bright, lush, modern. The default "indie pop" sound.</li>
            <li><strong>add11</strong> — a triad plus the 11th (same note as the 4th, an octave up). C add11 = C E G F′. Suspended, dreamy, slightly tense.</li>
          </ul>
          <p>
            And the crucial rule: an "add" chord <strong>does not</strong> have a 7th. That's
            what makes it different from a 9 chord (which we'll meet in chapter 22). A C9
            stacks four thirds — C E G B♭ D. A Cadd9 only stacks the triad and the 9th — C E
            G D, with no 7th in between. The add chord is "lighter" — open and uncluttered.
          </p>
        </Prose>

        <TheoryBox label="add9 vs. 9 — the difference">
          <ul className="space-y-1 text-[14px]">
            <li><strong>Cadd9</strong> = C E G D &nbsp; (triad + 9, <em>no 7th</em>)</li>
            <li><strong>C9</strong> = C E G B♭ D &nbsp; (triad + ♭7 + 9)</li>
            <li><strong>Cmaj9</strong> = C E G B D &nbsp; (triad + maj7 + 9)</li>
          </ul>
          The "add" prefix means: <em>add this one note, and skip the seventh</em>.
        </TheoryBox>
      </Chapter>

      <Chapter eyebrow="Why composers reach for add chords" title="Where add chords shine">
        <Prose>
          <p>
            Add chords are the secret behind the "open-string" sound of acoustic guitar
            ballads. The open Cadd9 shape every guitarist learns early (x32033) is exactly
            this — the triad with a D ringing on top. So is the open Gadd9, and so is the
            lush Eadd9 of Coldplay's piano voicings. Whenever a chord
            sounds "modern" without sounding "jazzy", an add9 is usually doing the work.
          </p>
          <p>
            <strong>add11</strong> is rarer but powerful. A C major chord with an F on top
            (Cadd11) sounds like Cmaj7sus — slightly suspended, slightly aching. Many film
            composers reach for it when they want a major chord that <em>doesn't</em> feel
            triumphant.
          </p>
          <p>
            And on minor triads, both adds work: <strong>m(add9)</strong> turns a sad
            minor chord into a wistful, beautiful one. <strong>m(add11)</strong> creates an
            even more melancholic, reflective tint.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="Build them" title="Stack the triad — then place the extras">
        <Prose>
          <p>
            Use the builder below to hear the difference between a plain triad and the
            same triad slashed or added. Every "add" sound in this lesson is one extra
            note glued onto a triad — no more, no less.
          </p>
        </Prose>

        <ChordBuilder families={["triad"]} />

        <Prose>
          <p>
            For the slash chord, just play any of the triads above and put a different
            note in your left hand. Try{" "}
            <span className="font-mono text-amber-300">C major over E, then over F, then
            over A</span>. Each bass change is a different slash chord — same triad, three
            distinct flavours.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="What's next" title="Stacking another third">
        <Prose>
          <p>
            We've now bolted things on to the triad without leaving the three-note family.
            The next chapter changes that. We add a <strong>fourth</strong> note — a third
            stacked on top of the fifth — and arrive at the world of <strong>seventh chords</strong>:
            major 7, dominant 7, minor 7, half-diminished, fully diminished. Pop, jazz,
            soul, gospel — every one of them runs on sevenths.
          </p>
        </Prose>
        <NoteImage
          src="/notes/music-theory/finals/20b-add-chords-chart.png"
          caption="Add chords: take the triad and add ONE extra note without stacking up to a 7th first. add9 adds the 9th. add4 adds the 4th. add♭6 adds the ♭6. Different from sus chords — add chords KEEP the 3rd, sus chords REPLACE it."
        />
      </Chapter>

      <NextUp
        to="/lesson/music-theory-21"
        label="Chapter 21 · Chord Formation - Seventh Chords"
        why="The triad becomes a four-note chord. Maj7, dom7, m7, m7♭5, dim7 — the harmonic vocabulary of jazz and gospel built from the same simple stack."
      />
    </LessonShell>
  );
}
